/**
 * Contexto de idioma y formato, y su hook.
 *
 * Separado de `I18nProvider.tsx` por la misma convención que `useApi.ts`: un
 * `.tsx` exporta componentes y nada más.
 */
import { createContext, useContext } from 'react';

import type { Formatters } from './format';
import type { Language } from './languages';
import type { Translate } from './translate';

export interface I18nValue {
  /** Idioma de los textos. Lo elige la persona. */
  language: Language;
  setLanguage: (language: Language) => void;
  /** Traduce una clave del catálogo. `t('users.count', { count: 12 })`. */
  t: Translate;
  /** Fechas, números y moneda en el locale y el huso del **tenant**. */
  fmt: Formatters;
}

export const I18nContext = createContext<I18nValue | null>(null);

/**
 * Textos y formato.
 *
 *     const { t, fmt } = useI18n();
 *     <h1>{t('alerts.inbox.title')}</h1>
 *     <td>{fmt.dateTime(alert.created_at)}</td>
 *
 * **Ninguna vista llama a `toLocaleDateString()` ni construye un `Intl` por
 * su cuenta**: eso usaría el locale del navegador y no el de la marca.
 */
export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === null) {
    throw new Error('useI18n() fuera de <I18nProvider>. Revisá el árbol de providers.');
  }
  return value;
}
